import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';

export interface ActivityLog {
  id: string;
  campaign_id: string;
  user_id: string;
  user_email: string;
  action: 'created' | 'updated' | 'deleted' | 'field_changed';
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface FieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  name: 'Campaign Name',
  brand: 'Brand',
  client_id: 'Client',
  campaign_status: 'Status',
  budget: 'Budget',
  start_date: 'Start Date',
  end_date: 'End Date',
  purchase_order: 'Purchase Order',
  campaign_ref: 'Reference',
  notes: 'Notes',
  campaign_mode: 'Campaign Mode',
  company_invoice_status: 'Company Invoice Status',
  client_payment_status: 'Client Payment Status',
  influencer_invoice_status: 'Influencer Invoice Status',
  influencer_payment_status: 'Influencer Payment Status',
  company_invoices: 'Company Invoices',
  influencer_invoices: 'Influencer Invoices',
  mohit_invoices: 'Mohit Invoices',
  deliverables: 'Deliverables',
  deliverables_status: 'Deliverables Status',
  agent_name: 'Agent',
  agent_id: 'Agent',
  fee: 'Fee',
  commission_pct: 'Commission %',
  campaign_budget: 'Campaign Budget',
  client_name: 'Client',
  influencer_name: 'Creator',
  delivery_date: 'Delivery Date',
  delivery_status: 'Delivery Status',
};

const EXCLUDED_FIELDS = new Set([
  'id',
  'created_at',
  'updated_at',
  'agent_id',
]);

function isUUID(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

function formatDeliverablesSummary(value: unknown): string {
  if (!value) return '-';

  try {
    const items = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(items)) return '-';

    if (items.length === 0) return 'No deliverables';

    const summary = items.slice(0, 2).map((item: { title?: string; content?: string; status?: string }) => {
      const name = item.title || item.content || 'Untitled';
      const shortName = name.length > 25 ? name.substring(0, 25) + '...' : name;
      return shortName;
    }).join(', ');

    if (items.length > 2) {
      return `${summary} +${items.length - 2} more`;
    }
    return summary;
  } catch {
    return '-';
  }
}

function formatInvoicesSummary(value: unknown): string {
  if (!value) return '-';

  try {
    const items = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(items)) return '-';

    if (items.length === 0) return 'No invoices';

    const summary = items.slice(0, 2).map((item: { number?: string; status?: string }) => {
      const num = item.number || 'Draft';
      const status = item.status || '';
      return status ? `${num} (${status})` : num;
    }).join(', ');

    if (items.length > 2) {
      return `${summary} +${items.length - 2} more`;
    }
    return summary;
  } catch {
    return '-';
  }
}

function formatValue(value: unknown, fieldName?: string): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    if (fieldName === 'commission_pct') {
      return `${(value * 100).toFixed(0)}%`;
    }
    if (fieldName === 'fee' || fieldName === 'campaign_budget' || fieldName === 'budget') {
      return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
    }
    return value.toLocaleString();
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      if (fieldName === 'deliverables') {
        return formatDeliverablesSummary(value);
      }
      if (fieldName === 'mohit_invoices' || fieldName === 'influencer_invoices' || fieldName === 'company_invoices') {
        return formatInvoicesSummary(value);
      }
      return `${value.length} item(s)`;
    }
    return JSON.stringify(value);
  }
  const strValue = String(value);
  if (fieldName === 'agent_id' && isUUID(strValue)) {
    return strValue;
  }
  return strValue;
}

function detectChanges(oldData: Record<string, unknown>, newData: Record<string, unknown>): FieldChange[] {
  const changes: FieldChange[] = [];
  const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

  for (const key of allKeys) {
    if (EXCLUDED_FIELDS.has(key)) continue;

    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldStr = JSON.stringify(oldVal);
    const newStr = JSON.stringify(newVal);

    if (oldStr !== newStr) {
      changes.push({
        field: key,
        oldValue: oldVal,
        newValue: newVal,
      });
    }
  }

  return changes;
}

export const activityLogService = {
  async getActivityLogs(campaignId: string): Promise<ActivityLog[]> {
    const { data, error } = await supabase
      .from('campaign_activity_logs')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async logCampaignCreated(
    campaignId: string,
    campaignName: string
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.rpc('log_campaign_change', {
      p_campaign_id: campaignId,
      p_user_id: user.id,
      p_user_email: user.email || 'Unknown',
      p_action: 'created',
      p_field_name: null,
      p_old_value: null,
      p_new_value: campaignName,
      p_details: {},
    });

    if (error) {
      log.error('Failed to log campaign creation:', error);
    }
  },

  async logCampaignDeleted(
    campaignId: string,
    campaignName: string
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.rpc('log_campaign_change', {
      p_campaign_id: campaignId,
      p_user_id: user.id,
      p_user_email: user.email || 'Unknown',
      p_action: 'deleted',
      p_field_name: null,
      p_old_value: campaignName,
      p_new_value: null,
      p_details: {},
    });

    if (error) {
      log.error('Failed to log campaign deletion:', error);
    }
  },

  async logCampaignChanges(
    campaignId: string,
    oldData: Record<string, unknown>,
    newData: Record<string, unknown>
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const changes = detectChanges(oldData, newData);

    if (changes.length === 0) return;

    const oneMinuteAgo = new Date(Date.now() - 60000).toISOString();
    const { data: recentLogs } = await supabase
      .from('campaign_activity_logs')
      .select('id, field_name, new_value')
      .eq('campaign_id', campaignId)
      .eq('user_id', user.id)
      .gte('created_at', oneMinuteAgo);

    const recentFieldChanges = new Map<string, { id: string; value: string }>();
    if (recentLogs) {
      for (const log of recentLogs) {
        if (log.field_name) {
          recentFieldChanges.set(log.field_name, { id: log.id, value: log.new_value || '' });
        }
      }
    }

    const changesToLog = changes.filter(change => {
      const recent = recentFieldChanges.get(change.field);
      if (recent) {
        const newFormattedValue = formatValue(change.newValue, change.field);
        if (recent.value === newFormattedValue) {
          return false;
        }
      }
      return true;
    });

    if (changesToLog.length === 0) return;

    const promises = changesToLog.map(change =>
      supabase.rpc('log_campaign_change', {
        p_campaign_id: campaignId,
        p_user_id: user.id,
        p_user_email: user.email || 'Unknown',
        p_action: 'field_changed',
        p_field_name: change.field,
        p_old_value: formatValue(change.oldValue, change.field),
        p_new_value: formatValue(change.newValue, change.field),
        p_details: {},
      })
    );

    const results = await Promise.allSettled(promises);

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        log.error(`Failed to log change for field ${changesToLog[index].field}:`, result.reason);
      }
    });
  },

  getFieldDisplayName(fieldName: string): string {
    return FIELD_DISPLAY_NAMES[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  },

  formatActivityValue(value: string | null, fieldName?: string): string {
    if (!value || value === '-') return '-';

    if (fieldName === 'agent_id' && isUUID(value)) {
      return value;
    }

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (fieldName === 'deliverables') {
          return formatDeliverablesSummary(parsed);
        }
        if (fieldName === 'mohit_invoices' || fieldName === 'influencer_invoices') {
          return formatInvoicesSummary(parsed);
        }
        return `${parsed.length} item(s)`;
      }
    } catch {
      // Not JSON, return as-is
    }

    return value;
  },

  async resolveAgentName(agentId: string): Promise<string | null> {
    if (!agentId || !isUUID(agentId)) return null;

    try {
      const { data } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', agentId)
        .maybeSingle();

      if (data) {
        return data.full_name || data.email?.split('@')[0] || null;
      }
    } catch {
      // Ignore errors
    }
    return null;
  },

  isAgentIdField(fieldName: string | null): boolean {
    return fieldName === 'agent_id';
  },
};
